import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import NavBar from '../../structure/NavBar'
import { NavBarTop } from '../../structure/NavBarTop'
import ApiCaller from '../../constants/ApiCaller'
import urls from '../../constants/apiurl'
import { uplodateLoading } from '../../../redux/features/common/commonSlice'
import { showDangerToast } from '../../constants/toaster'
import NoDataImg from '../../../assets/img/no-data.gif'
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Breadcrumb Component
const Breadcrumbs = ({ breadcrumbs, onClick }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <li
                            key={crumb.id || 'root'}
                            className={`breadcrumb-item ${isLast ? 'active' : ''}`}
                            aria-current={isLast ? 'page' : undefined}
                            style={{ cursor: isLast ? 'default' : 'pointer', color: isLast ? 'gray' : 'blue' }}
                            onClick={() => !isLast && onClick(index, crumb)}
                        >
                            {crumb.name}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export const UserLocationAnalytics = () => {
    const dispatch = useDispatch()
    const userData = useSelector(state => state?.userData)
    const LOADING_ENABLE = useSelector(state => state?.loading)

    const [data, setData] = useState([])
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'All', type: 'ROOT' }])

    const fetchData = async (parentId = null) => {
        try {
            dispatch(uplodateLoading(true))
            let params = {};
            if (parentId) {
                params.parentId = parentId;
            }

            let res = await ApiCaller.get(params, urls.userLocationAnalytics, userData?.access_token)

            if (res.status === 200) {
                const response = await res.response.json()
                if (response.data) {
                    setData(response.data);
                } else {
                    setData([]);
                }
            } else {
                showDangerToast('Failed to fetch data');
            }
        } catch (error) {
            console.error(error)
            showDangerToast(error.message)
        } finally {
            dispatch(uplodateLoading(false))
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleChartClick = (data) => {
        if (data && data.activePayload && data.activePayload.length > 0) {
            const payload = data.activePayload[0].payload;
            if (payload.type !== 'CITY') { // Don't drill down on leaf node
                const newBreadcrumbs = [...breadcrumbs, payload];
                setBreadcrumbs(newBreadcrumbs);
                fetchData(payload.id);
            }
        }
    }

    const handleBreadcrumbClick = (index, item) => {
        const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
        setBreadcrumbs(newBreadcrumbs);
        fetchData(item.id);
    }

    // Aggregating Gender Data for Pie Chart
    const genderData = [
        { name: 'Male', value: data.reduce((acc, curr) => acc + (curr.maleCount || 0), 0) },
        { name: 'Female', value: data.reduce((acc, curr) => acc + (curr.femaleCount || 0), 0) },
        { name: 'Other', value: data.reduce((acc, curr) => acc + (curr.otherCount || 0), 0) },
    ].filter(item => item.value > 0);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

    const handleCardClick = (item) => {
        if (item.type !== 'CITY') {
            const newBreadcrumbs = [...breadcrumbs, item];
            setBreadcrumbs(newBreadcrumbs);
            fetchData(item.id);
        }
    }

    // Sort data by totalCount descending
    const sortedData = [...data].sort((a, b) => (b.totalCount || 0) - (a.totalCount || 0));

    return (
        <>
            <NavBar />
            <div className='main-content'>
                <NavBarTop />
                <div className='header'>
                    <div className='logo'>
                        <div className='header bg-gradient-primary pb-8 pt-5 pt-md-8'></div>
                    </div>
                </div>

                <div className='container-fluid mt--7'>
                    <div className='row'>
                        <div className='col text-left'>
                            <div className='card shadow'>
                                <div className='card-header border-0'>
                                    <div className='row align-items-center'>
                                        <div className='col'>
                                            <h3 className='mb-0'>User Location Analytics</h3>
                                        </div>
                                    </div>
                                    <div className='row mt-3'>
                                        <div className='col'>
                                            <Breadcrumbs breadcrumbs={breadcrumbs} onClick={handleBreadcrumbClick} />
                                        </div>
                                    </div>
                                </div>

                                <div className='card-body'>
                                    {sortedData.length > 0 ? (
                                        <>
                                            {/* Summary Section with Pie Chart */}
                                            <div className="row mb-5 align-items-center">
                                                <div className="col-md-4 text-center">
                                                    <h4 className="text-muted text-uppercase ls-1 mb-1">Total Overview</h4>
                                                    <h2 className="mb-0">{sortedData[0]?.type || 'Location'} Level</h2>
                                                    <p className='mt-2'>Total Regions: {sortedData.length}</p>
                                                </div>
                                                <div className="col-md-8">
                                                    <div style={{ width: '100%', height: 300 }}>
                                                        <ResponsiveContainer>
                                                            <PieChart>
                                                                <Pie
                                                                    data={genderData}
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    innerRadius={60}
                                                                    outerRadius={100}
                                                                    paddingAngle={5}
                                                                    dataKey="value"
                                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                                >
                                                                    {genderData.map((entry, index) => (
                                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip />
                                                                <Legend verticalAlign="middle" align="right" layout="vertical" />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </div>

                                            <hr className='my-4' />

                                            {/* Cards Grid */}
                                            <h4 className="mb-4">Details by {sortedData[0]?.type || 'Location'} (Sorted by Count)</h4>
                                            <div className="row">
                                                {sortedData.map((item, index) => (
                                                    <div key={index} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                                        <div
                                                            className={`card h-100 border-0 shadow-sm ${item.type !== 'CITY' ? 'card-hover-effect' : ''}`}
                                                            style={{ cursor: item.type !== 'CITY' ? 'pointer' : 'default', transition: 'all 0.3s' }}
                                                            onClick={() => handleCardClick(item)}
                                                        >
                                                            <div className="card-body">
                                                                <h5 className="card-title text-uppercase text-muted mb-0">{item.type}</h5>
                                                                <span className="h2 font-weight-bold mb-0">{item.name}</span>

                                                                <div className="mt-3 mb-0 text-sm">
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <span className="text-nowrap font-weight-bold text-dark" style={{ fontSize: '1.2rem' }}>Total Users</span>
                                                                        <span className="font-weight-bold" style={{ fontSize: '2rem', color: '#5e72e4' }}>{item.totalCount}</span>
                                                                    </div>

                                                                    <div className="alert alert-secondary py-2 px-2 mb-3">
                                                                        <div className="d-flex justify-content-between text-xs font-weight-bold">
                                                                            <span className="text-success">Active: {item.activeCount || 0}</span>
                                                                            <span className="text-warning">Inactive: {item.inactiveCount || 0}</span>
                                                                            <span className="text-danger">Deleted: {item.deletedCount || 0}</span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="progress" style={{ height: '6px', marginBottom: '15px' }}>
                                                                        <div
                                                                            className="progress-bar bg-info"
                                                                            role="progressbar"
                                                                            style={{ width: `${(item.maleCount / item.totalCount) * 100}%` }}
                                                                            aria-valuenow={item.maleCount}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax={item.totalCount}
                                                                        ></div>
                                                                        <div
                                                                            className="progress-bar bg-success"
                                                                            role="progressbar"
                                                                            style={{ width: `${(item.femaleCount / item.totalCount) * 100}%` }}
                                                                            aria-valuenow={item.femaleCount}
                                                                            aria-valuemin="0"
                                                                            aria-valuemax={item.totalCount}
                                                                        ></div>
                                                                    </div>
                                                                    <div className='d-flex justify-content-between small' style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                                                        <span className="text-dark"><i className="fa fa-male text-info mr-1"></i> {item.maleCount}</span>
                                                                        <span className="text-dark"><i className="fa fa-female text-success mr-1"></i> {item.femaleCount}</span>
                                                                        <span className="text-dark text-muted"><i className="fa fa-genderless mr-1"></i> {item.otherCount}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Condensed Table for States (only when viewing States) */}
                                            {breadcrumbs.length === 2 && (
                                                <>
                                                    <hr className="my-5" />
                                                    <h4 className="mb-4">Detailed State List (High to Low)</h4>
                                                    <div className="table-responsive">
                                                        <table className="table align-items-center table-flush table-hover">
                                                            <thead className="thead-light">
                                                                <tr>
                                                                    <th scope="col" className="sort" data-sort="name">State Name</th>
                                                                    <th scope="col" className="sort" data-sort="total">Total Users</th>
                                                                    <th scope="col" className="sort" data-sort="active">Active</th>
                                                                    <th scope="col" className="sort" data-sort="inactive">Inactive</th>
                                                                    <th scope="col" className="sort" data-sort="deleted">Deleted</th>
                                                                    <th scope="col" className="sort" data-sort="male">Male</th>
                                                                    <th scope="col" className="sort" data-sort="female">Female</th>
                                                                    <th scope="col" className="sort" data-sort="other">Other</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="list">
                                                                {sortedData.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <th scope="row">
                                                                            <span className="name mb-0 text-sm font-weight-bold">{item.name}</span>
                                                                        </th>
                                                                        <td>
                                                                            <span className="badge badge-dot mr-4">
                                                                                <i className="bg-primary"></i>
                                                                                <span className="status font-weight-bold" style={{ fontSize: '1rem' }}>{item.totalCount}</span>
                                                                            </span>
                                                                        </td>
                                                                        <td><span className="text-success font-weight-bold">{item.activeCount || 0}</span></td>
                                                                        <td><span className="text-warning">{item.inactiveCount || 0}</span></td>
                                                                        <td><span className="text-danger">{item.deletedCount || 0}</span></td>
                                                                        <td>{item.maleCount}</td>
                                                                        <td>{item.femaleCount}</td>
                                                                        <td>{item.otherCount}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className='text-center py-5'>
                                            {LOADING_ENABLE ? (
                                                <label style={{ fontWeight: 'bold' }}>Loading Data.....</label>
                                            ) : (
                                                <>
                                                    <img width={'25%'} src={NoDataImg} alt="No Data"></img>
                                                    <br />
                                                    <label style={{ fontWeight: 'bold' }}>No Data Found</label>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className='card-footer py-4'></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .card-hover-effect:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 1rem 3rem rgba(0,0,0,.175)!important;
                    background-color: #f8f9fe;
                }
            `}</style>
        </>
    )
}
